import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { misAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { Card, Spinner } from '../common';

export const ManagerProductivitySection = ({ fullView = false }) => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await misAPI.managerTAT();
        if (isMounted && res.data?.data) {
          setData(res.data.data);
          // Trigger smooth filling animation after data mounts
          setTimeout(() => setAnimated(true), 150);
        }
      } catch (err) {
        // Free plan or feature locked: cleanly suppress error
        if (isMounted) setData(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [isAdmin]);

  // If not admin, or still loading, or feature locked on Free plan / no data: cleanly hide section
  if (!isAdmin || loading || !data) return null;

  const managers = data?.managers || [];
  const departments = data?.departments || [];

  const displayedManagers = fullView ? managers : managers.slice(0, 3);
  const displayedDepartments = fullView ? departments : departments.slice(0, 3);

  // Calculate overall stats for KPI chips
  const managersWithTat = managers.filter(m => m.avgTatHours > 0);
  const companyAvgTat = managersWithTat.length > 0 ? (data?.companyAvgTat ?? 2.4) : '—';

  const completedDepts = departments.filter(d => (d.completedCount || 0) > 0);
  const avgOnTime = completedDepts.length > 0
    ? Math.round(completedDepts.reduce((acc, d) => acc + (d.onTimeRate || 0), 0) / completedDepts.length) + '%'
    : '—';
  const totalBottlenecks = managers.filter(m => m.delayedCount > 0 || m.avgTatHours > 24).length;

  const handleExportCSV = () => {
    if (!data) return;
    const managersList = data.managers || [];
    const departmentsList = data.departments || [];

    const rows = [
      ['MANAGER REVIEW VELOCITY AND DEPARTMENT HEALTH REPORT'],
      ['Generated On', new Date().toLocaleString('en-IN')],
      ['Company Avg Review Speed (TAT)', companyAvgTat !== '—' ? `${companyAvgTat} hrs` : 'N/A'],
      ['Company On-Time Delivery Rate', avgOnTime],
      ['Total Manager Bottlenecks', totalBottlenecks],
      [],
      ['SECTION 1: DEPARTMENT MANAGERS REVIEW SPEED (TAT)'],
      ['Manager Name', 'Email', 'Department', 'Average Review TAT (Hours)', 'Approved Tasks', 'Delayed Reviews (>24h)', 'Pending Reviews', 'Performance Status'],
      ...(managersList.length === 0
        ? [['No department managers registered yet', '', '', '', '', '', '', '']]
        : managersList.map(m => [
            m.name || 'Unknown',
            m.email || 'N/A',
            m.departmentName || 'General Management',
            m.avgTatHours ? `${m.avgTatHours} hrs` : 'N/A',
            m.approvedCount ?? 0,
            m.delayedCount ?? 0,
            m.pendingReviewCount ?? 0,
            m.statusLabel || 'Normal'
          ])),
      [],
      ['SECTION 2: DEPARTMENT ON-TIME COMPLETION RATE AND HEALTH'],
      ['Department Name', 'Total Tasks', 'Completed Tasks', 'On-Time Completion Rate (%)', 'Pending / In-Progress Tasks'],
      ...(departmentsList.length === 0
        ? [['No departments found', '', '', '', '']]
        : departmentsList.map(d => [
            d.name || 'General',
            d.totalTasks ?? 0,
            d.completedCount ?? 0,
            d.completedCount > 0 ? `${d.onTimeRate ?? 0}%` : 'No tasks completed yet',
            Math.max(0, (d.totalTasks ?? 0) - (d.completedCount ?? 0))
          ]))
    ];

    const csvContent = rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Manager_Productivity_Report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Manager Productivity Report downloaded (CSV format)');
  };

  return (
    <Card className="p-5 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm hover:shadow-md transition-shadow mb-6">
      {/* Header with Mini KPIs */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b border-slate-100 dark:border-slate-800">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-7 h-7 rounded-lg bg-brand-500/10 text-brand-600 dark:text-brand-400 flex items-center justify-center font-bold text-sm">
              <svg className="w-4 h-4 text-brand-600 dark:text-brand-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
            </div>
            <h3 className="text-sm md:text-base font-extrabold text-slate-900 dark:text-white">
              Manager Review Velocity & Department Health
            </h3>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-brand-50 dark:bg-brand-950/50 text-brand-600 dark:text-brand-400 border border-brand-200 dark:border-brand-800">
              Admin Intelligence
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Live turnaround time (TAT) tracking for managers and on-time task delivery percentage
          </p>
        </div>

        {/* 3 Quick KPI Chips in Header */}
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          <div className="px-3 py-1.5 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200/60 dark:border-slate-700/60 flex items-center gap-2">
            <span className="text-[10px] font-semibold text-slate-400 uppercase">Avg Review</span>
            <span className="text-xs font-black text-emerald-600 dark:text-emerald-400">
              {companyAvgTat !== '—' ? `${companyAvgTat}h` : '—'}
            </span>
          </div>
          <div className="px-3 py-1.5 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200/60 dark:border-slate-700/60 flex items-center gap-2">
            <span className="text-[10px] font-semibold text-slate-400 uppercase">On-Time</span>
            <span className="text-xs font-black text-brand-600 dark:text-brand-400">{avgOnTime}</span>
          </div>
          <div className="px-3 py-1.5 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200/60 dark:border-slate-700/60 flex items-center gap-2">
            <span className="text-[10px] font-semibold text-slate-400 uppercase">Bottlenecks</span>
            <span className={`text-xs font-black ${totalBottlenecks > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {totalBottlenecks}
            </span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-8 flex items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 pt-4">
          
          {/* Column 1: Managers Leaderboard */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-extrabold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Department Managers Review Speed</span>
              </span>
              <span className="text-[11px] text-slate-400">{managers.length} Active Managers</span>
            </div>

            <div className="space-y-3">
              {displayedManagers.length === 0 ? (
                <p className="text-xs text-slate-400 italic py-3">No department managers registered yet.</p>
              ) : (
                displayedManagers.map((mgr) => {
                  const isTop = mgr.avgTatHours > 0 && mgr.avgTatHours <= 4;
                  const isBottleneck = mgr.avgTatHours > 24;
                  const hasReviewed = mgr.avgTatHours > 0;

                  return (
                    <div
                      key={mgr._id}
                      className={`group p-3 rounded-xl border flex items-center justify-between transition-all ${
                        isBottleneck
                          ? 'bg-red-500/5 dark:bg-red-950/20 border-red-500/30'
                          : isTop
                          ? 'bg-emerald-500/5 dark:bg-emerald-950/20 border-emerald-500/20'
                          : 'bg-slate-50/80 dark:bg-slate-800/50 hover:bg-slate-100/80 dark:hover:bg-slate-800 border-slate-200/60 dark:border-slate-700/50'
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className="w-9 h-9 rounded-xl text-white font-black text-xs flex items-center justify-center shrink-0 shadow-sm"
                          style={{ backgroundColor: mgr.departmentColor || '#6366f1' }}
                        >
                          {mgr.avatar ? (
                            <img src={mgr.avatar} alt={mgr.name} className="w-full h-full rounded-xl object-cover" />
                          ) : (
                            (mgr.name || 'M').charAt(0).toUpperCase()
                          )}
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-xs font-bold text-slate-900 dark:text-white flex items-center gap-1.5 truncate">
                            <span>{mgr.name}</span>
                            <span className="text-[10px] font-normal text-slate-500 bg-white dark:bg-slate-700/60 px-1.5 py-0.2 rounded border border-slate-200/60 dark:border-slate-600 truncate">
                              {mgr.departmentName}
                            </span>
                          </h4>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                            {mgr.approvedCount} approved ·{' '}
                            <span className={mgr.delayedCount > 0 ? 'text-red-500 font-bold' : 'text-slate-500'}>
                              {mgr.delayedCount} delayed
                            </span>
                          </p>
                        </div>
                      </div>

                      <div className="text-right shrink-0 ml-3">
                        {hasReviewed ? (
                          <span className={`text-xs font-black ${
                            isBottleneck ? 'text-red-600 dark:text-red-400' : isTop ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-700 dark:text-slate-300'
                          }`}>
                            Avg {mgr.avgTatHours}h
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-200/70 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                            {mgr.pendingReviewCount > 0 ? `${mgr.pendingReviewCount} Pending Review` : 'No Reviews Yet'}
                          </span>
                        )}
                        <p className={`text-[10px] font-bold mt-0.5 flex items-center justify-end gap-1 ${
                          isBottleneck ? 'text-red-500 animate-pulse' : isTop ? 'text-emerald-500' : 'text-slate-400'
                        }`}>
                          {isTop && (
                            <svg className="w-2.5 h-2.5 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          )}
                          {isBottleneck && (
                            <svg className="w-2.5 h-2.5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          )}
                          <span>{hasReviewed ? mgr.statusLabel : (mgr.pendingReviewCount > 0 ? 'Awaiting Action' : 'No Activity')}</span>
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Column 2: Department Health & Deadlines */}
          <div className="space-y-3 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between pb-3">
                <span className="text-xs font-extrabold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Department On-Time Completion Rate</span>
                </span>
                {!fullView && (
                  <button
                    onClick={() => navigate('/mis')}
                    className="text-[11px] font-bold text-brand-600 dark:text-brand-400 hover:underline"
                  >
                    {departments.length > 3 ? `Full MIS (+${departments.length - 3} more) →` : 'Full MIS Report →'}
                  </button>
                )}
              </div>

              <div className="space-y-3">
                {displayedDepartments.length === 0 ? (
                  <p className="text-xs text-slate-400 italic py-3">No departments created yet.</p>
                ) : (
                  displayedDepartments.map((dept) => {
                    const hasCompletedTasks = (dept.completedCount || 0) > 0;
                    const rate = hasCompletedTasks ? (dept.onTimeRate ?? 100) : 0;
                    const barColor = rate >= 90 ? 'from-emerald-500 to-teal-400' : rate >= 75 ? 'from-brand-500 to-indigo-500' : 'from-amber-500 to-amber-400';
                    const textColor = rate >= 90 ? 'text-emerald-600 dark:text-emerald-400' : rate >= 75 ? 'text-brand-600 dark:text-brand-400' : 'text-amber-600';

                    return (
                      <div
                        key={dept._id}
                        className="p-3 bg-slate-50/80 dark:bg-slate-800/50 rounded-xl border border-slate-200/60 dark:border-slate-700/50 space-y-2"
                      >
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <span
                              className="w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: dept.color || '#6366f1' }}
                            />
                            <span className="font-bold text-slate-900 dark:text-white">{dept.name}</span>
                            <span className="text-[11px] text-slate-400">
                              ({dept.completedCount || 0}/{dept.totalTasks || 0} completed)
                            </span>
                          </div>
                          <span className={`text-xs font-extrabold ${hasCompletedTasks ? textColor : 'text-slate-400'}`}>
                            {hasCompletedTasks ? `${rate}% On-Time` : 'No tasks yet'}
                          </span>
                        </div>

                        {/* Progress Bar with Animated Fill Effect */}
                        <div className="w-full h-2 bg-slate-200/80 dark:bg-slate-700 rounded-full overflow-hidden relative">
                          <div
                            className={`h-full bg-gradient-to-r ${barColor} rounded-full transition-all duration-1000 ease-out relative`}
                            style={{ width: `${animated && hasCompletedTasks ? rate : 0}%` }}
                          >
                            {hasCompletedTasks && (
                              <div className="absolute inset-0 bg-white/20 animate-pulse rounded-full" />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Bottom Action Footer - clean SVG icon without emoji */}
            <div className={`flex items-center ${!fullView ? 'justify-between' : 'justify-end'} pt-2 text-[11px] text-slate-500 dark:text-slate-400`}>
              {!fullView && (
                <button
                  onClick={() => navigate('/mis')}
                  className="font-medium text-slate-500 dark:text-slate-400 hover:text-brand-600 dark:hover:text-brand-400 hover:underline flex items-center gap-1 transition-colors"
                >
                  <span>View Full MIS Dashboard</span>
                </button>
              )}
              <button
                onClick={handleExportCSV}
                className="font-bold text-brand-600 dark:text-brand-400 hover:underline flex items-center gap-1.5 transition-colors"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Export Excel / CSV Report</span>
              </button>
            </div>
          </div>

        </div>
      )}
    </Card>
  );
};
